import {
  Button,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { UserSearchIcon, WalletIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import { z } from "zod";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { ROLE_FEE_KIND_LABELS } from "../../domain/contracts";
import { usePerformerQuery } from "../api/queries";

const StaffFormSchema = z.object({
  displayName: z.string().trim().min(1, "ຕ້ອງປ້ອນຊື່"),
  defaultRoleLabel: z.string().nullable().optional(),
  bankName: z.string().nullable().optional(),
  bankAccount: z.string().nullable().optional(),
  accountName: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  performerId: z.string().nullable().optional(),
  status: z.enum(["active", "inactive"]),
});

export type StaffFormValues = z.infer<typeof StaffFormSchema>;

type PerformerLookupItem = {
  id: string;
  name: string;
  defaultRoleFeeKind?: string;
};

type StaffFormProps = {
  initialValues?: Partial<StaffFormValues>;
  onSubmit: (values: StaffFormValues) => void;
  onCancel?: () => void;
  submitting?: boolean;
  isEdit?: boolean;
};

export function StaffForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  isEdit = false,
}: StaffFormProps) {
  const initialPerformerIdRef = useRef(initialValues?.performerId ?? null);

  const methods = RHF.useForm<StaffFormValues>({
    resolver: zodResolver(StaffFormSchema),
    defaultValues: {
      performerId: initialValues?.performerId ?? null,
      displayName: initialValues?.displayName ?? "",
      defaultRoleLabel: initialValues?.defaultRoleLabel ?? "",
      bankName: initialValues?.bankName ?? "",
      bankAccount: initialValues?.bankAccount ?? "",
      accountName: initialValues?.accountName ?? "",
      phone: initialValues?.phone ?? "",
      notes: initialValues?.notes ?? "",
      status: initialValues?.status ?? "active",
    },
  });

  const watchedPerformerId = methods.watch("performerId");
  const { data: performerDetail } = usePerformerQuery(watchedPerformerId ?? "");

  useEffect(() => {
    if (!performerDetail || !watchedPerformerId) return;
    if (
      isEdit &&
      watchedPerformerId === initialPerformerIdRef.current
    ) {
      return;
    }

    methods.setValue("displayName", performerDetail.name, { shouldDirty: true });
    methods.setValue("phone", performerDetail.phoneNumber ?? "", {
      shouldDirty: true,
    });
    methods.setValue("bankName", performerDetail.bankName ?? "", {
      shouldDirty: true,
    });
    methods.setValue("bankAccount", performerDetail.bankAccount ?? "", {
      shouldDirty: true,
    });
    methods.setValue("accountName", performerDetail.accountName ?? "", {
      shouldDirty: true,
    });
    methods.setValue("notes", performerDetail.notes ?? "", { shouldDirty: true });

    const roleLabel =
      ROLE_FEE_KIND_LABELS[
        performerDetail.defaultRoleFeeKind as keyof typeof ROLE_FEE_KIND_LABELS
      ];
    if (roleLabel) {
      methods.setValue("defaultRoleLabel", roleLabel, { shouldDirty: true });
    }
  }, [performerDetail, watchedPerformerId, isEdit, methods]);

  return (
    <FormRoot
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-6 py-6"
    >
      <div className="space-y-4 rounded-lg border bg-muted/20 p-4">
        <div className="flex items-center gap-2">
          <UserSearchIcon className="size-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm uppercase tracking-widest">
            ເຊື່ອມຕໍ່ນັກສະແດງ
          </h3>
        </div>
        <FormInfiniteCombobox<PerformerLookupItem>
          name="performerId"
          label="ເລືອກນັກສະແດງ"
          placeholder="ຄົ້ນຫາຊື່ນັກສະແດງ..."
          clearable
          queryKey={["performance", "performers", "lookup", "staff-link"]}
          queryFn={({ search, pageParam }) =>
            fetchLookupForInfinite<PerformerLookupItem>(
              `${config.apiUrl}/performers/lookup`,
              { search, pageParam },
            )
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem<PerformerLookupItem>(
              `${config.apiUrl}/performers/lookup`,
              id,
            ).then((item) => item ?? null)
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
        />
        <p className="text-muted-foreground text-xs">
          ເຊື່ອມຕໍ່ FK ກັບນັກສະແດງ — ເລືອກແລ້ວຈະເຕີມຂໍ້ມູນອັດຕະໂນມັດ (ແກ້ໄຂຄ່າໄດ້ກ່ອນບັນທຶກ)
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-sm uppercase tracking-widest">
          ຂໍ້ມູນພື້ນຖານ
        </h3>
        <FormInput name="displayName" label="ຊື່" requiredMark />
        <FormInput name="defaultRoleLabel" label="ບົດບາດເລີ່ມຕົ້ນ (ຖ້າມີ)" />
        <FormInput name="phone" label="ເບີໂທ" />
        <FormTextarea name="notes" label="ຫມາຍເຫດ" />
        {isEdit ? (
          <FormSelect
            name="status"
            label="ສະຖານະ"
            options={[
              { value: "active", label: "ໃຊ້ງານ" },
              { value: "inactive", label: "ປິດໃຊ້" },
            ]}
          />
        ) : null}
      </div>

      <div className="space-y-4 border-t pt-6">
        <div className="flex items-center gap-2">
          <WalletIcon className="size-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm uppercase tracking-widest">
            ບັນຊີທະນາຄານ
          </h3>
        </div>
        <FormInput name="bankName" label="ທະນາຄານ" />
        <FormInput name="bankAccount" label="ເລກບັນຊີ" />
        <FormInput name="accountName" label="ຊື່ບັນຊີ" />
      </div>

      <div className="flex justify-end gap-2 border-t pt-4">
        {onCancel ? (
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        ) : null}
        <Button type="submit" disabled={submitting}>
          {isEdit ? "ບັນທຶກ" : "ສ້າງ Staff"}
        </Button>
      </div>
    </FormRoot>
  );
}
